import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ClipboardPaste, Eye, Plus, Upload as UploadIcon, Zap } from "lucide-react";
import { leadsApi } from "@/api/leads";
import type {
  BrokerLeadStatsDto,
  BrokerLeadSummaryDto,
  InterestLevel,
  LeadStatus,
} from "@/api/leads";
import { useAssignableUsers } from "@/hooks/useAssignableUsers";
import { leadKeys } from "@/hooks/leads/leadKeys";
import { Badge, Button, StatCard, StatsBar } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { ListFilterBar } from "@/components/layout/ListFilterBar";
import { BulkActionBar } from "@/components/ux";
import { PageHeader } from "@/components/layout/PageHeader";
import { Pagination } from "@/components/layout/Pagination";
import { cn } from "@/design-system/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { useListFilters } from "@/hooks/useListFilters";
import { useDateFormatter } from "@/utils/datetime";
import { QuickAddLeadModal } from "./QuickAddLeadModal";
import { PasteToCreateLeadModal } from "./PasteToCreateLeadModal";
import { LEAD_FILTERS } from "./leadFilters";

const PAGE_SIZE = 25;
const EMPTY_STATS: BrokerLeadStatsDto = {
  total: 0,
  byStatus: {
    New: 0, Contacted: 0, Interested: 0, NotInterested: 0,
    Unresponsive: 0, HandedOff: 0, Disqualified: 0,
  },
};

const STATUS_BADGE: Record<LeadStatus, "draft" | "info" | "gold" | "finalized" | "archived" | "review" | "error"> = {
  New: "draft",
  Contacted: "info",
  Interested: "gold",
  NotInterested: "archived",
  Unresponsive: "archived",
  HandedOff: "finalized",
  Disqualified: "error",
};

const INTEREST_BADGE: Record<InterestLevel, "draft" | "info" | "gold" | "error"> = {
  Unknown: "draft",
  Cold: "info",
  Warm: "gold",
  Hot: "error",
};

export function LeadsPage() {
  const { hasPermission, hasRoleCode, user } = useAuth();
  const canCreate = hasPermission("Leads.Create");
  const canImport = hasPermission("Leads.Import");
  // Bulk archive is an ADMIN-only prerogative (single archive on the detail
  // page stays available to lead editors). The server enforces it regardless.
  const isAdmin = hasRoleCode("ADMIN");
  const queryClient = useQueryClient();
  const dialogs = useDialogs();
  const { date } = useDateFormatter();

  // Ownership/Assignment §5 — C5 marketers default to "My Leads"; managers /
  // admins (anyone with Leads.Update or Leads.Read across the org) see all.
  // We approximate "is C5 only" by NOT having any of the manager-tier perms.
  const isMarketerOnly = !hasPermission("Brokers.Update") && hasPermission("Leads.Read");

  // Every filter on this page now lives in the query string, via the shared
  // descriptor-driven shell (hooks/useListFilters + the LEAD_FILTERS
  // descriptor), so the view is bookmarkable, shareable and survives a refresh.
  // A filter sitting at its default writes no param, so a clean /leads load
  // issues exactly the requests it always did.
  //
  // The shell is COOPERATIVE about the URL: it only ever touches the params it
  // declares. ?importBatchId — the CSV import page's "View imported leads" deep
  // link, in the "N" format LeadImportService stamps onto
  // BrokerLead.ImportBatchId — is one of those declared params now, and any
  // param the descriptor does not know about survives a filter change.
  const filters = useListFilters(LEAD_FILTERS);
  const { q: search, status, source, state, importBatchId } = filters.values;
  const page = filters.page;

  const [onlyMine, setOnlyMine] = useState<boolean>(isMarketerOnly);
  const [quickOpen, setQuickOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const myUserId = user?.id;
  const assignedTo = onlyMine && myUserId ? myUserId : undefined;

  // Scope is deliberately NOT a URL filter. Its default comes from a permission
  // heuristic rather than from a param, and LeadsPage.test.tsx pins both that
  // default and the assignedTo it emits; reconciling it with the shared
  // MyToggle is the deferred lead-scoping decision, not part of this change. So
  // it stays local state — and resets the page itself, the way a descriptor
  // filter gets for free.
  const setScope = (mine: boolean) => {
    setOnlyMine(mine);
    filters.setPage(1);
  };

  // Map the dropdown to the API: the "Archived" sentinel sets archived=true and
  // omits the status param; a real status sets status and leaves archived off.
  const archivedView = status === "Archived";
  const statusParam: LeadStatus | undefined =
    status === "" || status === "Archived" ? undefined : status;
  // Multi-select bulk archive shows for admins on the active list (not in the
  // archived view — those leads are already archived).
  const canBulk = isAdmin && !archivedView;

  // Clear any bulk selection whenever the matched set changes so a stale
  // selected id can't survive into the next view. The page reset that used to
  // live here happens inside the same URL write as the filter change, so a
  // filter edit re-keys the queries once instead of twice.
  useEffect(() => {
    setSelectedIds([]);
  }, [filters.resetToken, assignedTo]);

  const list = useQuery({
    queryKey: leadKeys.list({ search, status, source, state, assignedTo, importBatchId, page }),
    queryFn: () =>
      leadsApi.list({
        page,
        pageSize: PAGE_SIZE,
        q: search || undefined,
        status: statusParam,
        source: source || undefined,
        state: state || undefined,
        assignedTo,
        importBatchId,
        archived: archivedView || undefined,
      }),
  });

  // Counts come from the server so they reflect the entire filtered set
  // (not just the current page). Status filter is intentionally omitted
  // — see BrokerLeadStatsDto.
  const statsQuery = useQuery({
    queryKey: leadKeys.stats({ search, source, state, assignedTo, importBatchId }),
    queryFn: () =>
      leadsApi.stats({
        q: search || undefined,
        source: source || undefined,
        state: state || undefined,
        assignedTo,
        importBatchId,
      }),
  });
  const stats = statsQuery.data ?? EMPTY_STATS;

  const assignableUsers = useAssignableUsers();
  const userNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of assignableUsers.data ?? []) m.set(u.id, u.displayName);
    return m;
  }, [assignableUsers.data]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: leadKeys.all });

  const bulkArchive = useMutation({
    mutationFn: (ids: string[]) => leadsApi.bulkArchive(ids),
    onSuccess: (count) => {
      setSelectedIds([]);
      invalidate();
      void dialogs.alert({
        title: "Leads archived",
        tone: "success",
        message: `${count} lead${count === 1 ? "" : "s"} archived.`,
      });
    },
    onError: (e: Error) => {
      void dialogs.alert({ title: "Bulk archive failed", tone: "error", message: e.message });
    },
  });

  const onBulkArchive = async (ids: string[]) => {
    const ok = await dialogs.confirm({
      title: "Archive selected leads?",
      message: `Archive ${ids.length} disqualified lead${ids.length === 1 ? "" : "s"}? ` +
        "They'll be hidden from the default list and counts, and can be unarchived later.",
      confirmLabel: "Archive",
    });
    if (ok) bulkArchive.mutate(ids);
  };

  const totalPages = useMemo(() => {
    const data = list.data;
    if (!data || data.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  }, [list.data]);

  const columns: Column<BrokerLeadSummaryDto>[] = useMemo(() => [
    {
      key: "name",
      header: "Name",
      render: (l) => (
        <Link to={`/leads/${l.id}`} className="font-semibold hover:text-gold-shadow">
          {l.firstName} {l.lastName}
          {l.companyName && <div className="text-caption text-gray-mid font-normal">{l.companyName}</div>}
        </Link>
      ),
    },
    {
      key: "contact",
      header: "Phone / Email",
      render: (l) => (
        <div className="text-small">
          <div className="font-mono text-caption">{l.phone ?? "—"}</div>
          <div className="text-gray-mid text-caption">{l.email ?? ""}</div>
        </div>
      ),
    },
    { key: "state", header: "State", render: (l) => l.state ?? "—" },
    { key: "source", header: "Source", render: (l) => <span className="text-caption">{l.source}</span> },
    { key: "status", header: "Status", render: (l) => <Badge status={STATUS_BADGE[l.status]}>{l.status}</Badge> },
    {
      key: "interest",
      header: "Interest",
      render: (l) => <Badge status={INTEREST_BADGE[l.interestLevel]}>{l.interestLevel}</Badge>,
    },
    {
      key: "contact-stats",
      header: "Calls",
      render: (l) => (
        <span className="font-mono text-caption">
          {l.totalCallAttempts} / {l.totalConnections}c
        </span>
      ),
    },
    {
      key: "last",
      header: "Last contact",
      render: (l) => l.lastContactDate
        ? <span className="text-caption">{date(l.lastContactDate)}</span>
        : <span className="text-caption text-gray-mid italic">never</span>,
    },
    {
      key: "assigned",
      header: "Assigned",
      render: (l) => (
        <span className="text-caption text-gray-mid">
          {userNameById.get(l.assignedMarketerUserId) ?? l.assignedMarketerUserId.slice(0, 8)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      className: "w-10 text-right",
      render: (l) => (
        <Link to={`/leads/${l.id}`} aria-label="Open lead">
          <Button variant="tertiary" size="sm"><Eye size={14} strokeWidth={1.5} aria-hidden /></Button>
        </Link>
      ),
    },
  ], [date, userNameById]);

  // Reskin of the former "My leads" checkbox — same onlyMine state, no behavior
  // change. Styled to match the brokers MyToggle.
  //
  // DELIBERATELY NOT components/ux/MyToggle, despite the matching styling. That
  // component hides itself for C0–C4 and derives its default from
  // primaryRoleCode (persisted in localStorage); this default comes from a
  // PERMISSION heuristic (isMarketerOnly) and /leads is reachable only by C0–C5
  // + ADMIN. Adopting it as-is would hide the control from five of the seven
  // roles that can open this page, and forceVisible only trades that for
  // flipping the landing tab (C0/C1 → Mine, C4/C5 → All). Reconciling the two
  // default rules is the deferred lead-scoping decision, not a styling cleanup
  // — LeadsPage.test.tsx pins the current default and assignedTo.
  const scopeToggle = (
    <div
      role="group"
      aria-label="Lead scope"
      className="inline-flex shrink-0 rounded-md border border-border-light bg-white-pure overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setScope(true)}
        aria-pressed={onlyMine}
        className={cn(
          "h-8 px-space-3 inline-flex items-center text-label font-display font-bold uppercase tracking-button transition-colors duration-fast ease-standard",
          onlyMine ? "bg-gold text-onyx hover:bg-gold-highlight" : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10"
        )}
      >
        My leads
      </button>
      <button
        type="button"
        onClick={() => setScope(false)}
        aria-pressed={!onlyMine}
        className={cn(
          "h-8 px-space-3 inline-flex items-center text-label font-display font-bold uppercase tracking-button transition-colors duration-fast ease-standard",
          !onlyMine ? "bg-gold text-onyx hover:bg-gold-highlight" : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10"
        )}
      >
        All
      </button>
    </div>
  );

  return (
    <>
      <PageHeader
        eyebrow="Partners"
        title="Leads"
        actions={
          <div className="flex items-center gap-space-2">
            {canImport && (
              <Link to="/leads/import">
                <Button variant="secondary" size="sm">
                  <UploadIcon size={14} strokeWidth={1.5} aria-hidden /> Import CSV
                </Button>
              </Link>
            )}
            {canCreate && (
              <Button size="sm" variant="secondary" onClick={() => setQuickOpen(true)}>
                <Zap size={14} strokeWidth={1.5} aria-hidden /> Quick add
              </Button>
            )}
            {canCreate && (
              <Button size="sm" variant="secondary" onClick={() => setPasteOpen(true)}>
                <ClipboardPaste size={14} strokeWidth={1.5} aria-hidden /> Paste to create
              </Button>
            )}
            {canCreate && (
              <Link to="/leads/new">
                <Button size="sm">
                  <Plus size={14} strokeWidth={1.5} aria-hidden /> New lead
                </Button>
              </Link>
            )}
          </div>
        }
      />

      <LeadStatsBar stats={stats} />

      {importBatchId && (
        <div
          className="flex items-center gap-space-2 mb-space-3 px-space-3 py-space-2 rounded-md border border-border-light bg-white-warm text-small"
          role="status"
        >
          <span>
            Showing only leads from one CSV import batch. The counts above cover
            the same batch.
          </span>
          <Button
            variant="text"
            size="sm"
            onClick={() => filters.set("importBatchId", undefined)}
          >
            Show all leads
          </Button>
        </div>
      )}

      <ListFilterBar
        filters={filters}
        rows={[
          // Row 1 deliberately does not wrap: the search input grows into
          // whatever the scope toggle leaves, exactly as it did before.
          { className: "flex items-center gap-space-3", before: scopeToggle, keys: ["q"] },
          { keys: ["status", "source", "state"] },
        ]}
      />

      {canBulk && (
        <BulkActionBar
          selectedIds={selectedIds}
          totalRows={list.data?.totalCount}
          onClear={() => setSelectedIds([])}
          label="Lead bulk actions"
          className="mb-space-3"
          actions={[
            {
              key: "archive",
              label: bulkArchive.isPending ? "Archiving…" : "Archive",
              icon: <Archive size={14} strokeWidth={1.5} aria-hidden />,
              primary: true,
              disabled: bulkArchive.isPending,
              onClick: (ids) => onBulkArchive(ids),
            },
          ]}
        />
      )}

      <DataTable
        columns={columns}
        rows={list.data?.data ?? []}
        loading={list.isLoading}
        error={list.isError ? (list.error as Error).message : null}
        emptyMessage="No leads match your filters."
        rowKey={(l) => l.id}
        selectedIds={canBulk ? selectedIds : undefined}
        onSelectionChange={canBulk ? setSelectedIds : undefined}
        // Only disqualified, not-yet-archived leads can be bulk-archived; other
        // rows render a disabled checkbox. The server skips ineligible ids too.
        isRowSelectable={(l) => l.status === "Disqualified" && !l.isArchived}
      />

      {list.data && list.data.totalCount > 0 && (
        <Pagination
          page={list.data.page}
          pageSize={list.data.pageSize}
          totalCount={list.data.totalCount}
          totalPages={totalPages}
          onPrev={() => filters.setPage(Math.max(1, page - 1))}
          onNext={() => filters.setPage(Math.min(totalPages, page + 1))}
        />
      )}

      {quickOpen && (
        <QuickAddLeadModal
          onClose={() => setQuickOpen(false)}
          onCreated={() => { invalidate(); }}
        />
      )}

      {pasteOpen && (
        <PasteToCreateLeadModal
          onClose={() => setPasteOpen(false)}
          onCreated={invalidate}
        />
      )}
    </>
  );
}

function LeadStatsBar({ stats }: { stats: BrokerLeadStatsDto }) {
  return (
    <StatsBar columns={5}>
      <StatCard label="Total" value={stats.total} />
      <StatCard label="New" value={stats.byStatus.New} />
      <StatCard label="Contacted" value={stats.byStatus.Contacted} />
      <StatCard label="Interested" value={stats.byStatus.Interested} />
      <StatCard label="Handed off" value={stats.byStatus.HandedOff} />
    </StatsBar>
  );
}

